import { AccordionGroup } from '@nautilusbio/nb-design-system';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { type ChangeEvent, type FC, memo } from 'react';

import styles from '../../styles.module.css';

import { ORG_LIST_MAX_HEIGHT_PX, SEARCH_THRESHOLD } from './constants';
import { CurrentOrgSection } from './CurrentOrgSection';
import { useOrgSearch } from './hooks';
import { OrgListItem } from './OrgListItem';
import type { OrgSelectorContentProps } from './types';

/**
 * Adaptive popover body for the org selector.
 *
 * - Always shows the current org section (name, role toggle, copy-ID).
 * - If there are other orgs: shows a "Switch organization" section.
 * - If there are 6+ other orgs: shows a search/filter input above the list.
 * - The org list scrolls independently at a capped height.
 */
export const OrgSelectorContent: FC<OrgSelectorContentProps> = memo(
  ({
    selectedOrg,
    otherOrgs,
    allowedRoles,
    currentRole,
    allowedRolesByOrg,
    assumedRolesByOrg,
    onSwitch,
    onRoleSwitch,
    onCopyOrgId,
    isPending,
    tOrgs,
  }) => {
    const { query, setQuery, filtered } = useOrgSearch(otherOrgs);
    const showSearch = otherOrgs.length >= SEARCH_THRESHOLD;

    const searchLabel = {
      children: tOrgs('org_selector_search_placeholder'),
      id: 'org-search-label',
      htmlFor: 'org-search-input',
    };

    return (
      <Stack gap={4} orientation="vertical">
        <CurrentOrgSection
          allowedRoles={allowedRoles}
          currentRole={currentRole}
          isPending={isPending}
          onCopyOrgId={onCopyOrgId}
          onRoleSwitch={onRoleSwitch}
          selectedOrg={selectedOrg}
          tOrgs={tOrgs}
        />

        {otherOrgs.length > 0 && (
          <>
            <hr className={styles.divider} />

            <Text as="h6">{tOrgs('org_selector_switch')}</Text>

            {showSearch && (
              <Input
                data-testid="org-search-input"
                fullWidth
                id="org-search-input"
                label={searchLabel}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                placeholder={tOrgs('org_selector_search_placeholder')}
                size="xs2"
                value={query}
              />
            )}

            <AccordionGroup
              allowsMultipleExpanded
              className={styles.orgListContainer}
              orientation="vertical"
              style={{ maxHeight: ORG_LIST_MAX_HEIGHT_PX }}
            >
              {filtered.map(org => (
                <OrgListItem
                  key={org.id}
                  allowedRoles={allowedRolesByOrg[org.id] ?? []}
                  assumedRole={assumedRolesByOrg[org.id]}
                  isPending={isPending}
                  org={org}
                  onSwitch={onSwitch}
                />
              ))}
              {filtered.length === 0 && <Text as="span">{tOrgs('org_selector_no_results')}</Text>}
            </AccordionGroup>
          </>
        )}
      </Stack>
    );
  },
);

OrgSelectorContent.displayName = 'OrgSelectorContent';
